import { useCallback, useMemo } from 'react';
import { useDateFormatter } from 'react-aria';
import type { CalendarState } from 'react-stately';
import { maxYear, MIN_YEAR } from '../../constants';
import type { Month, Year } from '../types';
import { CalendarView } from '../types';

interface UseManualCalendarProps {
  readonly state: CalendarState;
  readonly calendarView: CalendarView;
  readonly setCalendarView: (view: CalendarView) => void;
  readonly isSideBySideCalendarView?: boolean;
  readonly minNumberOfVisibleYears?: number;
}

interface UseManualCalendarReturnType {
  readonly months: Month[];
  readonly years: Year[];
  readonly daysView: boolean;
  readonly onMonthChange: (month: number) => void;
  readonly onYearChange: (index: number) => void;
  readonly isYearSelected: (year: Year) => boolean;
  readonly isMonthSelected: (month: Month) => boolean;
}

export const useManualCalendar = ({
  state,
  calendarView,
  setCalendarView,
  isSideBySideCalendarView,
  minNumberOfVisibleYears = 0,
}: UseManualCalendarProps): UseManualCalendarReturnType => {
  const yearsFormatter = useDateFormatter({
    year: 'numeric',
    timeZone: state.timeZone,
  });

  const monthsFormatter = useDateFormatter({
    month: 'long',
    timeZone: state.timeZone,
  });

  const isYearDisabled = useCallback(
    (year: number): boolean => year < (state.minValue?.year ?? 0) || year > (state.maxValue?.year ?? 0),
    [state.maxValue, state.minValue],
  );

  const { minValue, maxValue } = state;
  const currentMinYear = minValue?.year ?? MIN_YEAR;
  const currentMaxYear = maxValue?.year ?? maxYear;

  const years = useMemo(() => {
    const visibleYears = Array.from(
      { length: Math.max(currentMaxYear - currentMinYear + 1, minNumberOfVisibleYears) },
      (_, index) => currentMinYear + index,
    );

    return visibleYears.map(year => {
      const date: Date = new Date(year, 0, 1);
      return {
        value: date,
        formatted: yearsFormatter.format(date),
        isDisabled: isYearDisabled(year),
      };
    });
  }, [currentMaxYear, currentMinYear, yearsFormatter, isYearDisabled, minNumberOfVisibleYears]);

  const monthsInYear = useMemo(
    () => state.focusedDate.calendar.getMonthsInYear(state.focusedDate),
    [state.focusedDate],
  );

  const months = useMemo(() => {
    return Array.from({ length: monthsInYear }, (_, monthIndex) => {
      const monthStartDate = state.focusedDate.set({ month: monthIndex + 1, day: 1 });
      const daysInMonth = state.focusedDate.calendar.getDaysInMonth(monthStartDate);
      const monthEndDate = monthStartDate.set({ day: daysInMonth });
      const isDisabled =
        (state.maxValue != null && monthStartDate > state.maxValue) ||
        (state.minValue != null && monthEndDate < state.minValue);
      return {
        value: monthsFormatter.format(monthStartDate.toDate(state.timeZone)),
        formatted: monthsFormatter.format(monthStartDate.toDate(state.timeZone)),
        isDisabled,
      };
    });
  }, [monthsFormatter, monthsInYear, state]);

  const onMonthChange = useCallback(
    (month: number): void => {
      const date = state.focusedDate.set({ month: month + 1 });
      state.setFocusedDate(date);
      if (isSideBySideCalendarView) {
        return;
      }

      setCalendarView(CalendarView.Day);
    },
    [isSideBySideCalendarView, setCalendarView, state],
  );

  const onYearChange = useCallback(
    (index: number): void => {
      const year = Number(years[index].formatted);
      const date = state.focusedDate.set({ year });
      state.setFocusedDate(date);
      if (isSideBySideCalendarView) {
        return;
      }

      setCalendarView(CalendarView.Month);
    },
    [isSideBySideCalendarView, setCalendarView, state, years],
  );

  const isYearSelected = useCallback(
    (year: Year): boolean => year.formatted === state.focusedDate.toDate(state.timeZone).getFullYear().toString(),
    [state],
  );

  const isMonthSelected = useCallback(
    (month: Month): boolean => {
      const date = state.focusedDate.toDate(state.timeZone);
      return month.formatted === monthsFormatter.format(date);
    },
    [monthsFormatter, state],
  );

  return {
    months,
    isYearSelected,
    onMonthChange,
    isMonthSelected,
    onYearChange,
    years,
    daysView: calendarView === CalendarView.Day,
  };
};
